import React, {
  useMemo,
  useState,
  ReactNode,
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
} from "react";

import { getAPIErrorDetailAndMessage } from "@/api/helpers";
import { useTranslationLang } from "@/i18n";
import { loadPreferredLang } from "@/i18n/preferredLang";
import type { SupportedLangs } from "@/i18n/types";
import { datadogRum } from "@datadog/browser-rum";
import { useAppRouteParams } from "../../../AppRoutes";
import { getAccount, postEditAccountLocale } from "../../../api/accounts";
import {
  AccountRaw,
  getIsAAAdminOrAbove,
  getIsAADeveloperOrAbove,
  getIsAAMemberOrAbove,
} from "../../../domain/Account";
import { DialogPhaseType, useDialog } from "../../Dialog/helpers/hooks";
import { useAppAuth } from "../AppAuthProvider";

const accountCtx = createContext<{
  account: AccountRaw | null;
  runReloadAccount: () => Promise<void>;
  isLoadedAccount: boolean;
  setAccount: React.Dispatch<React.SetStateAction<AccountRaw | null>>;
  persistAccountLocale: (locale: SupportedLangs) => void;
  isAAMemberOrAbove: boolean;
  isAAAdminOrAbove: boolean;
  isAADeveloperOrAbove: boolean;
}>({
  account: null,
  runReloadAccount: async () => {
    //nop
  },
  isLoadedAccount: false,
  setAccount: () => {
    //nop
  },
  persistAccountLocale: () => {
    //nop
  },
  isAAMemberOrAbove: false,
  isAAAdminOrAbove: false,
  isAADeveloperOrAbove: false,
});

/**
 * @description view, sheet, AppTopNavなどさまざまな場所で使うaccountデータを管理
 */
export const AppAccountProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { user } = useAppAuth();
  const { setLang } = useTranslationLang();
  const { organizationId } = useAppRouteParams();

  const [account, setAccount] = useState<AccountRaw | null>(null);
  const [_, setError] = useState<unknown>(null);

  const [dialogPhase, setDialogPhase] = useState<DialogPhaseType>(null);
  const { setIsInProgress, isInProgress } = useDialog({
    dialogPhase,
    setDialogPhase,
  });

  const { isAAMemberOrAbove, isAAAdminOrAbove, isAADeveloperOrAbove } =
    useMemo(() => {
      const isAAMemberOrAbove = getIsAAMemberOrAbove(account);
      const isAADeveloperOrAbove = getIsAADeveloperOrAbove(account);
      const isAAAdminOrAbove = getIsAAAdminOrAbove(account);
      return { isAAMemberOrAbove, isAAAdminOrAbove, isAADeveloperOrAbove };
    }, [account]);

  const desiredLocaleRef = useRef<SupportedLangs | null>(null);
  const isPersistingLocaleRef = useRef(false);

  const persistAccountLocale = useCallback(
    async (locale: SupportedLangs) => {
      desiredLocaleRef.current = locale;
      if (!user || isPersistingLocaleRef.current) return;

      isPersistingLocaleRef.current = true;
      try {
        // 並行して投げるとサーバー側の最終値が古い方で確定しうるため、送り直して直列化する
        let sendingLocale: SupportedLangs | null = desiredLocaleRef.current;
        while (sendingLocale) {
          const newAccount = await postEditAccountLocale({
            uid: user.uid,
            locale: sendingLocale,
          });
          if (desiredLocaleRef.current === sendingLocale) {
            desiredLocaleRef.current = null;
            setAccount(newAccount);
          }
          sendingLocale = desiredLocaleRef.current;
        }
      } catch {
        desiredLocaleRef.current = null;
      } finally {
        isPersistingLocaleRef.current = false;
      }
    },
    [user]
  );

  // 言語はブラウザに保存した選択値を優先し、account 側を追従させる。
  // ログイン画面で選んだ言語がログイン後も引き継がれるようにするため。
  const syncLangWithFetchedAccount = useCallback(
    (fetchedAccount: AccountRaw) => {
      const selectedLang = loadPreferredLang();
      if (!selectedLang) {
        // ブラウザから推測した言語は本人の表明ではないため書き戻さない
        if (fetchedAccount.locale) setLang(fetchedAccount.locale);
        return;
      }
      if (fetchedAccount.locale !== selectedLang)
        persistAccountLocale(selectedLang);
    },
    [setLang, persistAccountLocale]
  );

  const runReloadAccount = useCallback(async () => {
    if (!user) return;
    try {
      setIsInProgress(true);
      const response = await getAccount({
        uid: user?.uid,
      });
      setAccount(response);
      syncLangWithFetchedAccount(response);
      setIsInProgress(false);
    } catch (err: unknown) {
      setError(() => {
        if (typeof err == "object") {
          const e = {
            ...err,
            message: getAPIErrorDetailAndMessage(err),
          } as Error;
          throw e;
        } else {
          throw new Error("不明なエラー");
        }
      });
    }
  }, [getAccount, organizationId, user, syncLangWithFetchedAccount]);

  useEffect(() => {
    datadogRum.setUserProperty("userKind", account?.user_kind ?? "null");
  }, [account]);

  const accountCtxValue = useMemo(
    () => ({
      account,
      runReloadAccount,
      isLoadedAccount: !isInProgress,
      setAccount,
      persistAccountLocale,
      isAAMemberOrAbove,
      isAAAdminOrAbove,
      isAADeveloperOrAbove,
    }),
    [
      account,
      runReloadAccount,
      isInProgress,
      persistAccountLocale,
      isAAMemberOrAbove,
      isAAAdminOrAbove,
      isAADeveloperOrAbove,
    ]
  );

  useEffect(() => {
    runReloadAccount();
  }, [runReloadAccount]);

  return (
    <accountCtx.Provider value={accountCtxValue}>
      {children}
    </accountCtx.Provider>
  );
};

export const useAppAccount = () => {
  return useContext(accountCtx);
};
